/**
 * Ponds, tanks, reservoirs and rain-harvesting pits around a coordinate, modelled
 * the way the Damodarpura pits are: a basin carved into the terrain with the water
 * standing in it month by month. Everything is live from public sources:
 *
 *   outline   JRC Global Surface Water occurrence, 1984-2021, 30 m (Planetary
 *             Computer): where water has stood and how often. A pixel wet more
 *             often is a deeper part of the basin.
 *   monthly   Sentinel-2 L2A, the clearest scene of each month, 10 m: the water
 *             spread that month - ESA's scene classification says "water", or
 *             MNDWI > 0.2 with SWIR reflectance under 0.05. Since 2022 the bands
 *             carry a +1000 offset, removed before the index.
 *   rain      Open-Meteo ERA5 archive, daily, summed by month.
 *
 * Nobody has surveyed the depth of these. The basin shape and the monthly spread
 * are measured; the full depth is an assumption by kind, which the user can
 * change. Volumes scale with that depth, so changing it is a multiplication.
 */
import maplibregl from 'maplibre-gl';
import { YEAR, type TerrainCarver } from './site3d';

const PC = 'https://planetarycomputer.microsoft.com/api';
/** Half-size of the modelled box, degrees of latitude (about 2.2 km each way). */
const HALF = 0.02;
const PIXEL_M = 10;

export type WaterKind = 'Rain-harvesting / farm pond' | 'Pond / tank' | 'Lake / reservoir' | 'Stream / canal';

/** Assumed full depth (m) and the distance from the bank to full depth (m), by kind. */
export const KIND_DEFAULTS: Record<WaterKind, { depth: number; reach: number }> = {
  'Rain-harvesting / farm pond': { depth: 3, reach: 5 },
  'Pond / tank': { depth: 3, reach: 15 },
  'Lake / reservoir': { depth: 6, reach: 60 },
  'Stream / canal': { depth: 0, reach: 1 },
};

export interface WaterBody {
  id: number;
  kind: WaterKind;
  /** Full outline, m2. */
  areaMax: number;
  center: [number, number];
  /** Highest JRC occurrence in the body, %; 0 = not in the 1984-2021 record (new, or too small for 30 m). */
  occurrence: number;
  /** Assumed full depth, m. */
  depth: number;
  /** m3 per metre of full depth; capacity = unitCapacity * depth. */
  unitCapacity: number;
  /** Water spread per month (Jan..Oct), m2; null where the month had no clear view of it. */
  areas: (number | null)[];
  /** Stored water per month per metre of full depth; stored = unitStored * depth. */
  unitStored: (number | null)[];
}

export interface WaterModel {
  key: string;
  /** west, south, east, north */
  bbox: [number, number, number, number];
  w: number;
  h: number;
  /** Pixel size, m. */
  px: number;
  /** Body id per pixel, 0 = none. */
  label: Int32Array;
  /** Depth below the full level as a fraction of the body's full depth, 0..1. */
  frac: Float32Array;
  /** Per month: 0 dry, 1 water, 255 not seen (cloud, shadow, no data); null = no scene. */
  months: (Uint8Array | null)[];
  scenes: (string | null)[];
  rain: (number | null)[];
  bodies: WaterBody[];
}

export const capacity = (b: WaterBody): number => b.unitCapacity * b.depth;
export const stored = (b: WaterBody, m: number): number | null => (b.unitStored[m] == null ? null : (b.unitStored[m] as number) * b.depth);

// ------------------------------------------------------------ fetching

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`${url} ${r.status}`);
  return (await r.json()) as T;
}

/** RGBA pixels of a PNG, exactly as stored (no colour management, no premultiplying). */
async function pixels(url: string, w: number, h: number): Promise<Uint8ClampedArray> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`image ${r.status}`);
  const bmp = await createImageBitmap(await r.blob(), { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no canvas');
  ctx.drawImage(bmp, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h).data;
}

type Feature = { id: string; properties: { datetime: string } };

async function monthMask(bboxStr: string, bbox: number[], m: number, w: number, h: number): Promise<{ mask: Uint8Array; scene: string } | null> {
  const t0 = new Date(Date.UTC(YEAR, m, 1)).toISOString();
  const t1 = new Date(Date.UTC(YEAR, m + 1, 1)).toISOString();
  const s = await postJson<{ features: Feature[] }>(`${PC}/stac/v1/search`, {
    collections: ['sentinel-2-l2a'], bbox, datetime: `${t0}/${t1}`, limit: 1,
    query: { 'eo:cloud_cover': { lt: 50 } }, sortby: [{ field: 'eo:cloud_cover', direction: 'asc' }],
  });
  const item = s.features[0];
  if (!item) return null;
  // B03 and B11 from DN 1000 (= reflectance 0, the 2022+ offset) to 3000 (= 0.2); SCL as is
  const px = await pixels(`${PC}/data/v1/item/bbox/${bboxStr}/${w}x${h}.png?collection=sentinel-2-l2a&item=${item.id}`
    + '&expression=B03;B11;SCL&asset_as_band=True&rescale=1000,3000&rescale=1000,3000&rescale=0,255', w, h);
  const mask = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const g = px[i * 4] ?? 0, sw = px[i * 4 + 1] ?? 0, scl = px[i * 4 + 2] ?? 0, a = px[i * 4 + 3] ?? 0;
    // no data, saturated, cloud shadow, cloud
    if (a === 0 || scl === 0 || scl === 1 || scl === 3 || scl === 8 || scl === 9) { mask[i] = 255; continue; }
    const mndwi = (g - sw) / Math.max(g + sw, 1);
    mask[i] = scl === 6 || (mndwi > 0.2 && sw < 64) ? 1 : 0; // 64/255 of 0.2 = 0.05 reflectance
  }
  return { mask, scene: item.properties.datetime.slice(0, 10) };
}

async function occurrence(bboxStr: string, bbox: number[], w: number, h: number): Promise<Uint8Array> {
  const out = new Uint8Array(w * h);
  const s = await postJson<{ features: Feature[] }>(`${PC}/stac/v1/search`, { collections: ['jrc-gsw'], bbox, limit: 1 });
  const item = s.features[0];
  if (!item) return out;
  const px = await pixels(`${PC}/data/v1/item/bbox/${bboxStr}/${w}x${h}.png?collection=jrc-gsw&item=${item.id}&assets=occurrence`, w, h);
  for (let i = 0; i < w * h; i++) {
    const v = px[i * 4] ?? 0;
    out[i] = (px[i * 4 + 3] ?? 0) > 0 && v <= 100 ? v : 0;
  }
  return out;
}

async function monthlyRain(lat: number, lon: number): Promise<(number | null)[]> {
  const end = new Date(Date.now() - 6 * 86400e3).toISOString().slice(0, 10);
  const r = await fetch(`https://archive-api.open-meteo.com/v1/archive?latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}`
    + `&start_date=${YEAR}-01-01&end_date=${end < `${YEAR}-10-31` ? end : `${YEAR}-10-31`}&daily=precipitation_sum&timezone=Asia%2FKolkata`);
  if (!r.ok) return Array(10).fill(null);
  const d = ((await r.json()) as { daily?: { time: string[]; precipitation_sum: (number | null)[] } }).daily;
  const sums: (number | null)[] = Array(10).fill(null);
  d?.time.forEach((t, i) => {
    const m = Number(t.slice(5, 7)) - 1;
    if (m < 10) sums[m] = (sums[m] ?? 0) + (d.precipitation_sum[i] ?? 0);
  });
  return sums;
}

// ------------------------------------------------------------ the model

/** Runs `fn` over `items` with at most `n` at a time. */
async function pool<T, R>(items: T[], n: number, fn: (t: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(n, items.length) }, async () => {
    while (next < items.length) { const i = next++; out[i] = await fn(items[i] as T); }
  }));
  return out;
}

export async function buildWaterModel(lat: number, lon: number, progress: (note: string) => void): Promise<WaterModel> {
  const k = Math.cos((lat * Math.PI) / 180);
  const dLon = HALF / k;
  const bbox: [number, number, number, number] = [lon - dLon, lat - HALF, lon + dLon, lat + HALF];
  const w = Math.round((2 * dLon * 111320 * k) / PIXEL_M), h = Math.round((2 * HALF * 110574) / PIXEL_M);
  const px = (2 * HALF * 110574) / h;
  const bboxStr = bbox.map((v) => v.toFixed(5)).join(',');
  let done = 0;
  progress('Reading the 1984-2021 water record…');
  const [occ, rain, monthly] = await Promise.all([
    occurrence(bboxStr, bbox, w, h).catch(() => new Uint8Array(w * h)),
    monthlyRain(lat, lon).catch(() => Array(10).fill(null) as (number | null)[]),
    pool([...Array(10).keys()], 4, (m) => monthMask(bboxStr, bbox, m, w, h).catch(() => null).finally(() => {
      done++; progress(`Reading Sentinel-2, ${done} of 10 months…`);
    })),
  ]);
  const months = monthly.map((r) => r?.mask ?? null);
  const scenes = monthly.map((r) => r?.scene ?? null);

  // where water has been: in the long record, or seen this year (twice, so a lone shadow is not a pond)
  const n = w * h;
  const wetCount = new Uint8Array(n);
  for (const mk of months) if (mk) for (let i = 0; i < n; i++) if (mk[i] === 1) wetCount[i] = (wetCount[i] ?? 0) + 1;
  const ever = new Uint8Array(n);
  for (let i = 0; i < n; i++) ever[i] = (occ[i] ?? 0) > 0 || (wetCount[i] ?? 0) >= 2 ? 1 : 0;

  // bodies = 4-connected pieces
  const label = new Int32Array(n);
  const pieces: number[][] = [];
  for (let s = 0; s < n; s++) {
    if (!ever[s] || label[s]) continue;
    const id = pieces.length + 1, cells = [s];
    label[s] = id;
    for (let q = 0; q < cells.length; q++) {
      const c = cells[q] as number, x = c % w, y = (c - x) / w;
      for (const nb of [x > 0 ? c - 1 : -1, x < w - 1 ? c + 1 : -1, y > 0 ? c - w : -1, y < h - 1 ? c + w : -1]) {
        if (nb >= 0 && ever[nb] && !label[nb]) { label[nb] = id; cells.push(nb); }
      }
    }
    pieces.push(cells);
  }

  // distance to the bank, in pixels (chamfer 3-4 / 3)
  const dist = new Float32Array(n).fill(0);
  const INF = 1e9;
  for (let i = 0; i < n; i++) dist[i] = label[i] ? INF : 0;
  const at = (x: number, y: number): number => (x < 0 || y < 0 || x >= w || y >= h ? 0 : (dist[y * w + x] as number));
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x;
    if (!label[i]) continue;
    dist[i] = Math.min(dist[i] as number, at(x - 1, y) + 3, at(x, y - 1) + 3, at(x - 1, y - 1) + 4, at(x + 1, y - 1) + 4);
  }
  for (let y = h - 1; y >= 0; y--) for (let x = w - 1; x >= 0; x--) {
    const i = y * w + x;
    if (!label[i]) continue;
    dist[i] = Math.min(dist[i] as number, at(x + 1, y) + 3, at(x, y + 1) + 3, at(x + 1, y + 1) + 4, at(x - 1, y + 1) + 4);
  }

  const frac = new Float32Array(n);
  const bodies: WaterBody[] = [];
  const [west, , , north] = bbox;
  pieces.forEach((cells, idx) => {
    const id = idx + 1;
    const occPx = cells.filter((c) => (occ[c] ?? 0) > 0).length;
    if (cells.length < 3 && occPx < 2) { cells.forEach((c) => { label[c] = 0; }); return; }
    const areaMax = cells.length * px * px;
    let edge = 0, sx = 0, sy = 0, x0 = w, x1 = 0, y0 = h, y1 = 0, occMax = 0;
    for (const c of cells) {
      const x = c % w, y = (c - x) / w;
      sx += x; sy += y; x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y);
      occMax = Math.max(occMax, occ[c] ?? 0);
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1 || !label[c - 1] || !label[c + 1] || !label[c - w] || !label[c + w]) edge++;
    }
    const compact = (4 * Math.PI * cells.length) / Math.max(edge, 1) ** 2;
    const extent = Math.max(x1 - x0 + 1, y1 - y0 + 1) * px;
    const kind: WaterKind = compact < 0.15 && extent > 300 ? 'Stream / canal'
      : areaMax < 2500 ? 'Rain-harvesting / farm pond' : areaMax < 50000 ? 'Pond / tank' : 'Lake / reservoir';
    const { depth, reach } = KIND_DEFAULTS[kind];
    // basin shape: the bank slopes to full depth over `reach`; where the long record covers
    // the body, how often a pixel was wet shapes the bed too (wet more often = deeper)
    const useOcc = occPx >= cells.length * 0.3 && occMax > 0;
    const fr: number[] = [];
    for (const c of cells) {
      const edgeFrac = Math.min(1, ((((dist[c] as number) / 3) - 0.5) * px) / reach);
      const f = useOcc ? 0.5 * edgeFrac + 0.5 * ((occ[c] ?? 0) / occMax) : edgeFrac;
      frac[c] = Math.max(0.02, Math.min(1, f));
      fr.push(frac[c] as number);
    }
    const unitCapacity = fr.reduce((a, b) => a + b, 0) * px * px;
    const sorted = [...fr].sort((a, b) => b - a);
    const areas: (number | null)[] = [], unitStored: (number | null)[] = [];
    months.forEach((mk) => {
      if (!mk) { areas.push(null); unitStored.push(null); return; }
      let wet = 0, unseen = 0;
      for (const c of cells) { if (mk[c] === 1) wet++; else if (mk[c] === 255) unseen++; }
      if (unseen > cells.length * 0.3) { areas.push(null); unitStored.push(null); return; }
      areas.push(wet * px * px);
      // the level that wets as many pixels as were seen wet, and the water above the bed below it
      const z = wet >= sorted.length ? 0 : (sorted[wet] as number);
      let v = 0;
      for (let i = 0; i < Math.min(wet, sorted.length); i++) v += (sorted[i] as number) - z;
      unitStored.push(v * px * px);
    });
    bodies.push({
      id, kind, areaMax, occurrence: occMax, depth, unitCapacity, areas, unitStored,
      center: [west + ((sx / cells.length + 0.5) / w) * (bbox[2] - west), north - ((sy / cells.length + 0.5) / h) * (north - bbox[1])],
    });
  });
  bodies.sort((a, b) => b.areaMax - a.areaMax);
  return { key: `${lat.toFixed(4)},${lon.toFixed(4)}`, bbox, w, h, px, label, frac, months, scenes, rain, bodies };
}

// ------------------------------------------------------------ drawing

const lonOf = (u: number, n: number): number => (u / n) * 360 - 180;
const latOf = (v: number, n: number): number => (Math.atan(Math.sinh(Math.PI * (1 - (2 * v) / n))) * 180) / Math.PI;

/** Model grid cell under each pixel of tile z/x/y (-1 off the grid), row by row. */
function tileCells(model: WaterModel, z: number, x: number, y: number): Int32Array {
  const [west, south, east, north] = model.bbox;
  const n = 2 ** z;
  const out = new Int32Array(65536).fill(-1);
  for (let j = 0; j < 256; j++) {
    const row = Math.floor(((north - latOf(y + (j + 0.5) / 256, n)) / (north - south)) * model.h);
    if (row < 0 || row >= model.h) continue;
    for (let i = 0; i < 256; i++) {
      const col = Math.floor(((lonOf(x + (i + 0.5) / 256, n) - west) / (east - west)) * model.w);
      if (col >= 0 && col < model.w) out[j * 256 + i] = row * model.w + col;
    }
  }
  return out;
}

// The month's picture is served as map tiles drawn from the model on request (waterimg://).
// A single draped image does not survive MapLibre 4.7's terrain: an image whose covering
// tile is coarser than the terrain's zoom is skipped ("cannot calculate elevation...").
let picture: { model: WaterModel; month: number; pick: number | null } | null = null;
let pictureVersion = 0;
let protocolOn = false;

/** What the water tiles show: water blue, the rest of each basin pale. Returns the new tile version. */
export function setWaterPicture(p: { model: WaterModel; month: number; pick: number | null } | null): number {
  picture = p;
  return ++pictureVersion;
}
export const waterTiles = (): string[] => [`waterimg://v${pictureVersion}/{z}/{x}/{y}`];

async function drawTile(z: number, x: number, y: number): Promise<ArrayBuffer> {
  const cv = new OffscreenCanvas(256, 256);
  const ctx = cv.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  const p = picture;
  if (p) {
    const { model, month, pick } = p;
    const shown = new Set(model.bodies.map((b) => b.id));
    const mk = model.months[month] ?? null;
    const img = ctx.createImageData(256, 256);
    const cells = tileCells(model, z, x, y);
    for (let i = 0; i < 65536; i++) {
      const g = cells[i] as number;
      const id = g < 0 ? 0 : (model.label[g] as number);
      if (!id || !shown.has(id)) continue;
      const o = i * 4, s = mk ? mk[g] : 255;
      if (s === 1) { img.data[o] = 40; img.data[o + 1] = 130; img.data[o + 2] = 235; img.data[o + 3] = 215; }
      else { img.data[o] = 170; img.data[o + 1] = 215; img.data[o + 2] = 255; img.data[o + 3] = pick === id ? 150 : s === 255 ? 60 : 95; }
    }
    ctx.putImageData(img, 0, 0);
  }
  return (await cv.convertToBlob({ type: 'image/png' })).arrayBuffer();
}

export function ensureWaterProtocol(): void {
  if (protocolOn) return;
  protocolOn = true;
  maplibregl.addProtocol('waterimg', async (params) => {
    const m = /^waterimg:\/\/v\d+\/(\d+)\/(\d+)\/(\d+)/.exec(params.url);
    if (!m) throw new Error(`bad water url ${params.url}`);
    return { data: await drawTile(Number(m[1]), Number(m[2]), Number(m[3])) };
  });
}

/**
 * Cuts each basin into the terrain: the ground (which over water is the water
 * surface) is lowered by the body's full depth times the pixel's depth fraction.
 * Streams are left as they are.
 */
export function waterCarver(model: WaterModel): TerrainCarver {
  const [west, south, east, north] = model.bbox;
  const depthOf = new Map(model.bodies.filter((b) => b.kind !== 'Stream / canal').map((b) => [b.id, b.depth]));
  return {
    covers: (z, x, y) => {
      const n = 2 ** z;
      return !(lonOf(x + 1, n) < west || lonOf(x, n) > east || latOf(y, n) < south || latOf(y + 1, n) > north);
    },
    apply: (z, x, y, heights) => {
      const cells = tileCells(model, z, x, y);
      for (let i = 0; i < 65536; i++) {
        const g = cells[i] as number;
        const d = g < 0 ? undefined : depthOf.get(model.label[g] as number);
        if (d) heights[i] = (heights[i] as number) - d * (model.frac[g] as number);
      }
    },
  };
}
